"use client";

import { IonButtons, IonContent, IonHeader, IonMenuButton, IonPage, IonTitle, IonToolbar, IonLoading, IonButton, } from "@ionic/react"; 
import type React from "react"; 
import { useEffect, useState } from "react"; 
import { Link, useHistory, useLocation } from "react-router-dom"; 
import { Avatar, Box, Button, Card, CardContent, CardHeader, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField, Table, TableHead, TableRow, TableCell, TableBody, Typography, Chip, Paper, Divider, Stack, Container, Fade, Zoom, Pagination, Breadcrumbs, IconButton, alpha, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Alert, } from "@mui/material"; 
import { PhotoCamera, Visibility, Add, School, AttachMoney, Description, Person, ManageAccounts, BookmarkBorder, Home, NavigateNext, Search, FilterList, Close as CloseIcon, } from "@mui/icons-material"; 
import axios from "axios"; 
import { requestID, baseUrl, config } from "../../../common/utils";

const CreateCourse: React.FC = () => { 
  const [showCourseModal, setShowCourseModal] = useState(false); 
  const [editingCourseId, setEditingCourseId] = useState<string | null>(null); 
  const [newCourseName, setNewCourseName] = useState(""); 
  const [newCourseImage, setNewCourseImage] = useState<File | null>(null); 
  const [newCourseImageUrl, setNewCourseImageUrl] = useState(""); 
  const [newCourseAuthor, setNewCourseAuthor] = useState(""); 
  const [newCourseDescription, setNewCourseDescription] = useState(""); 
  const [newCourseType, setNewCourseType] = useState("free"); 
  const [amount, setAmount] = useState(""); 
  const [newCourseCertificateType, setNewCourseCertificateType] = useState("certificate"); 
  const [isLoading, setIsLoading] = useState(false); 
  const [status, setStatus] = useState({ loading: false, error: false, message: "", }); 
  const [courseList, setCourselist] = useState([]); 
  const [page, setPage] = useState(1); 
  const [rowsPerPage] = useState(5); 
  const [searchQuery, setSearchQuery] = useState(""); 
  const [formErrors, setFormErrors] = useState({ courseName: "", author: "", description: "", amount: "", }); 
  const theme = useTheme();

  const history = useHistory();

  function handleClick() { 
    console.log("hello"); 
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setNewCourseImage(file);
      setNewCourseImageUrl(URL.createObjectURL(file));
    }
  };

  // Form validation function 
  const validateForm = () => { 
    const errors = { 
      courseName: "", 
      author: "", 
      description: "", 
      amount: "", 
    };

    if (!newCourseName.trim()) {
      errors.courseName = "Course name is required";
    }

    if (!newCourseAuthor.trim()) {
      errors.author = "Author name is required";
    }

    if (!newCourseDescription.trim()) {
      errors.description = "Description is required";
    }

    if (
      newCourseType === "paid" &&
      (!amount.trim() || isNaN(Number(amount)) || Number(amount) <= 0)
    ) {
      errors.amount = "Valid amount is required for paid courses";
    }

    setFormErrors(errors);
    return (
      !errors.courseName &&
      !errors.author &&
      !errors.description &&
      !errors.amount
    );
  };

  const resetForm = () => { 
    setNewCourseName(""); 
    setNewCourseImage(null); 
    setNewCourseImageUrl(""); 
    setNewCourseAuthor(""); 
    setNewCourseDescription(""); 
    setNewCourseType("free"); 
    setAmount(""); 
    setNewCourseCertificateType("certificate"); 
    setEditingCourseId(null); 
    setFormErrors({ courseName: "", author: "", description: "", amount: "", }); 
  };

  const handleOpenCreateCourse = () => { 
    setShowCourseModal(true); 
  };

  const handleCloseCreateCourse = () => { 
    setShowCourseModal(false); 
    resetForm(); 
  };

  const handleAddOrEditCourse = async () => { 
    if (!validateForm()) { 
      return; 
    }

    const certOption = newCourseCertificateType === "certificate" ? 1 : 0;
    const attestOption = newCourseCertificateType === "attestation" ? 1 : 0;

    try {
      setIsLoading(true);
      setStatus({ loading: true, error: false, message: "Creating course..." });

      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("name", newCourseName);
      formData.append("status", "1");
      formData.append("amount", newCourseType === "paid" ? amount : "0");
      formData.append("author", newCourseAuthor);
      formData.append("description", newCourseDescription);
      formData.append("paymenttype", newCourseType);
      formData.append("certOption", certOption.toString());
      formData.append("attestOption", attestOption.toString());
      if (newCourseImage) {
        formData.append("image", newCourseImage);
      }

      const response = await axios.post(
        baseUrl() + "courses/addCourse",
        formData,
        config
      );

      console.log(response);
      setStatus({
        loading: false,
        error: false,
        message: "Course created successfully!",
      });
    } catch (error) {
      console.error("Error:", error);
      setStatus({
        loading: false,
        error: true,
        message: "Failed to create course",
      });
    } finally {
      setIsLoading(false);
      getAllCourses(); // Refresh the course list
      setTimeout(() => {
        handleCloseCreateCourse(); // Close popup after successful creation
      }, 1500);
    }
  };

  useEffect(() => { 
    getAllCourses(); 
  }, []);

  async function getAllCourses() { 
    setIsLoading(true); 
    setStatus({ loading: true, error: false, message: "" });

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        baseUrl() + "courses/getAllCourses",
        formData,
        config
      );
      console.log(response.data.data);
      setCourselist(response.data.data);
    } catch (error) {
      console.error("API call failed:", error);
      setStatus({
        loading: false,
        error: true,
        message: "Failed to load courses",
      });
    } finally {
      setIsLoading(false);
      setStatus({ loading: false, error: false, message: "" });
    }
  }

  const location = useLocation();

  useEffect(() => { 
    console.log("🔹 location object:", location); 
  }, [history, location]);

  const queryParams = new URLSearchParams(location.search); 
  const id = queryParams.get("location.search"); 
  const data = location.state?.["location"] || {};

  // Pagination logic 
  const filteredCourses = courseList.filter((course) => 
    course.name.toLowerCase().includes(searchQuery.toLowerCase()) 
  );

  const pageCount = Math.ceil(filteredCourses.length / rowsPerPage); 
  const paginatedCourses = filteredCourses.slice( 
    (page - 1) * rowsPerPage, 
    page * rowsPerPage 
  );

  const handlePageChange = ( 
    event: React.ChangeEvent<unknown>, 
    value: number 
  ) => { 
    setPage(value); 
  };

  // FIXED: Corrected navigation path
  const handleNavigate = (cosID: string) => {
    console.log("Navigating to module with cosID:", cosID);
    // Navigate to the module page with the correct route and course data
    history.push(`/folder/module/?id=${cosID}`, { 
      role: { 
        cosID: cosID,
        name: courseList.find(course => course.cosID === cosID)?.name || "Course"
      } 
    });
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Create Course</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">Create Course</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonLoading
          isOpen={isLoading}
          message={status.message || "Please wait..."}
          spinner="circles"
        />

        <div>{data.search}</div>

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Create Course Dialog/Popup */}
          <Dialog
            open={showCourseModal}
            onClose={handleCloseCreateCourse}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 4,
                background: "white",
                boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              },
            }}
          >
            <DialogTitle>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center">
                  <School sx={{ mr: 2, color: "#6366F1", fontSize: 32 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {editingCourseId ? "Edit Course" : "Create New Course"}
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseCreateCourse}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent>
              {status.message && (
                <Alert
                  severity={status.error ? "error" : "success"}
                  sx={{ mb: 3 }}
                >
                  {status.message}
                </Alert>
              )}

              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Course Name"
                    value={newCourseName}
                    onChange={(e) => {
                      setNewCourseName(e.target.value);
                      if (e.target.value.trim()) {
                        setFormErrors({ ...formErrors, courseName: "" });
                      }
                    }}
                    error={!!formErrors.courseName}
                    helperText={formErrors.courseName}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <School color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 4px 20px rgba(99, 102, 241, 0.3)",
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Author"
                    value={newCourseAuthor}
                    onChange={(e) => {
                      setNewCourseAuthor(e.target.value);
                      if (e.target.value.trim()) {
                        setFormErrors({ ...formErrors, author: "" });
                      }
                    }}
                    error={!!formErrors.author}
                    helperText={formErrors.author}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 4px 20px rgba(99, 102, 241, 0.3)",
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={4}
                    value={newCourseDescription}
                    onChange={(e) => {
                      setNewCourseDescription(e.target.value);
                      if (e.target.value.trim()) {
                        setFormErrors({ ...formErrors, description: "" });
                      }
                    }}
                    error={!!formErrors.description}
                    helperText={formErrors.description}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{ alignSelf: "flex-start", mt: 2 }}
                        >
                          <Description color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 4px 20px rgba(99, 102, 241, 0.3)",
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Course Type</InputLabel>
                    <Select
                      value={newCourseType}
                      onChange={(e) =>
                        setNewCourseType(e.target.value as string)
                      }
                      label="Course Type"
                      startAdornment={
                        <InputAdornment position="start">
                          <BookmarkBorder color="primary" />
                        </InputAdornment>
                      }
                      sx={{
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 4px 20px rgba(99, 102, 241, 0.3)",
                        },
                      }}
                    >
                      <MenuItem value="free">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip label="Free" color="success" size="small" />
                          <Typography>Free Course</Typography>
                        </Stack>
                      </MenuItem>
                      <MenuItem value="paid">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip label="Paid" color="primary" size="small" />
                          <Typography>Paid Course</Typography>
                        </Stack>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {newCourseType === "paid" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      value={amount}
                      onChange={(e) => {
                        setAmount(e.target.value);
                        if (
                          e.target.value.trim() &&
                          !isNaN(Number(e.target.value)) &&
                          Number(e.target.value) > 0
                        ) {
                          setFormErrors({ ...formErrors, amount: "" });
                        }
                      }}
                      error={!!formErrors.amount}
                      helperText={formErrors.amount}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoney color="primary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          },
                          "&.Mui-focused": {
                            boxShadow: "0 4px 20px rgba(99, 102, 241, 0.3)",
                          },
                        },
                      }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={newCourseType === "paid" ? 12 : 6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Certificate Type</InputLabel>
                    <Select
                      value={newCourseCertificateType}
                      onChange={(e) =>
                        setNewCourseCertificateType(
                          e.target.value as "certificate" | "attestation"
                        )
                      }
                      label="Certificate Type"
                      sx={{
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 4px 20px rgba(99, 102, 241, 0.3)",
                        },
                      }}
                    >
                      <MenuItem value="certificate">Certificate</MenuItem>
                      <MenuItem value="attestation">Attestation</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      fontWeight={600}
                      color="text.primary"
                    >
                      Course Image
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={3}>
                      <Button
                        variant="contained"
                        component="label"
                        startIcon={<PhotoCamera />}
                        size="large"
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          px: 3,
                          py: 1.5,
                          background:
                            "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                          boxShadow: "0 4px 15px rgba(99, 102, 241, 0.4)",
                          "&:hover": {
                            boxShadow: "0 6px 20px rgba(99, 102, 241, 0.6)",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Choose Image
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageUpload}
                        />
                      </Button>
                      {newCourseImageUrl && (
                        <Paper elevation={3} sx={{ p: 1, borderRadius: 2 }}>
                          <Avatar
                            src={newCourseImageUrl}
                            variant="rounded"
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 2,
                            }}
                          />
                        </Paper>
                      )}
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 2 }}>
              <Button
                onClick={handleCloseCreateCourse}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddOrEditCourse}
                disabled={isLoading}
                variant="contained"
                size="large"
                startIcon={editingCourseId ? <Visibility /> : <Add />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  background:
                    "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                  boxShadow: "0 4px 15px rgba(99, 102, 241, 0.4)",
                  "&:hover": {
                    boxShadow: "0 6px 20px rgba(99, 102, 241, 0.6)",
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": {
                    background: "linear-gradient(135deg, #ccc 0%, #999 100%)",
                    boxShadow: "none",
                    transform: "none",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {editingCourseId ? "Update Course" : "Create Course"}
              </Button>
            </DialogActions>
          </Dialog>

          <Fade in timeout={1000}>
            <Card
              elevation={0}
              sx={{
                mt: 0,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 4px 24px rgba(0,0,0,0.05)",
                background: "white",
              }}
            >
              <CardHeader
                title={
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="h5" fontWeight={700}>
                      Your Courses ({courseList.length})
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Search courses..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(1);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: 250 }}
                    />

                    <Button
                      variant="contained"
                      onClick={handleOpenCreateCourse}
                      startIcon={<Add />}
                      size="large"
                      sx={{
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: 600,
                        px: 2,
                        py: 1,
                        background:
                          "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        "&:hover": {
                          background: "rgba(99, 102, 241, 0.4)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Create New Course
                    </Button>
                  </Box>
                }
                subheader={
                  <Typography variant="body2" color="text.secondary">
                    Manage and organize your course content
                  </Typography>
                }
              />
              <CardContent sx={{ p: 0 }}>
                {courseList.length > 0 ? (
                  <>
                    <Table sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "grey.50" }}>
                          <TableCell sx={{ fontWeight: 700, py: 2 }}>
                            Course Name
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, py: 2 }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedCourses.map((role, index) => (
                          <TableRow
                            key={index}
                            hover
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                              "&:hover": {
                                bgcolor: "action.hover",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: "primary.main",
                                    width: 40,
                                    height: 40,
                                  }}
                                >
                                  <School />
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                  >
                                    {role.name}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <IonButton
                                onClick={() => handleNavigate(role.cosID)}
                                style={{
                                  borderRadius: "12px",
                                  fontWeight: 600,
                                  textTransform: "none",
                                  background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                                  color: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(99, 102, 241, 0.4)";
                                  e.currentTarget.style.transform = "translateY(-1px)";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.boxShadow = "none";
                                  e.currentTarget.style.transform = "translateY(0)";
                                }}
                              >
                                <ManageAccounts style={{ fontSize: "20px" }} />
                                Manage
                              </IonButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <Box display="flex" justifyContent="center" p={3}>
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        shape="rounded"
                        sx={{
                          "& .MuiPaginationItem-root": {
                            borderRadius: 2,
                          },
                          "& .Mui-selected": {
                            background:
                              "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                            color: "white",
                            fontWeight: 600,
                          },
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <Box
                    sx={{
                      p: 6,
                      textAlign: "center",
                      bgcolor: "grey.50",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: "primary.main",
                        mx: "auto",
                        mb: 3,
                      }}
                    >
                      <School sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      No courses yet
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Create your first course to get started with building
                      amazing learning experiences
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleOpenCreateCourse}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        background:
                          "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                        "&:hover": {
                          boxShadow: "0 4px 15px rgba(99, 102, 241, 0.4)",
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      Create Your First Course
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Container>

        <style>
          {`
          ion-page {
            --ion-background-color: #f8fafc;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }

          ion-toolbar {
            --background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%);
            --color: #ffffff;
            --padding-top: 16px;
            --padding-bottom: 16px;
            box-shadow: 0 4px 20px rgba(99, 102, 241, 0.3);
          }

          ion-title {
            font-size: 1.5rem;
            font-weight: 700;
            letter-spacing: -0.025em;
          }

          ion-content {
            --background: #f8fafc;
          }

          ion-loading {
            --background: rgba(0, 0, 0, 0.8);
            --spinner-color: #6366F1;
          }
        `}
        </style>
      </IonContent>
    </IonPage>
  ); 
};

export default CreateCourse;